// The tracer lines (`BulletTracers::DrawTracerLine` 0xd9b420; crates/sim/src/present/effects/
// bullet_tracers.rs): the sim records each step's lines as LINE events, the original draws them
// through `IXRenderManager::DrawLine` (vtable slot 0x45, `XRenderManagerImpl<OpenGLImpl>::DrawLine`
// 0x1144d40 → `XPrimitiveSystem::DrawLine` 0x113c100) into the primitive layer of
// `FWDP_Transparent:WS_RenderGeometry_GameMID`, rendered after the layer's geometry
// (`XRenderManagerImpl::RenderFrame`) as `GL_LINES` of `glLineWidth(width)` pixels
// (`__Platform_RenderBatchLines` 0x1141e20) with the colour per vertex, no depth test and blend
// SRC_ALPHA, ONE_MINUS_SRC_ALPHA (`__SetRenderState` 0x1141510, flags 0). WebGL has no wide
// lines, so each line is a quad widened by `width` pixels along the screen's minor axis (GL's
// rule for aliased wide lines). A frame shows the lines of its own step only (the primitive
// layers are rebuilt every frame).
import * as THREE from 'three';
import { LINE, type LineEvent, type SoundEvent } from '../sim/view.ts';

const VERT = /* glsl */ `
attribute vec3 other;
attribute float side;
attribute vec4 lineColour;
attribute float lineWidth;
uniform vec2 viewport;
varying vec4 vColour;
void main() {
  vec4 a = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  vec4 b = projectionMatrix * modelViewMatrix * vec4(other, 1.0);
  vec2 d = (b.xy / b.w - a.xy / a.w) * viewport;
  vec2 minor = abs(d.x) >= abs(d.y) ? vec2(0.0, 1.0) : vec2(1.0, 0.0);
  a.xy += minor * side * lineWidth / viewport * a.w;
  gl_Position = a;
  vColour = lineColour;
}`;

const FRAG = /* glsl */ `
varying vec4 vColour;
void main() { gl_FragColor = vColour; }`;

export class TracerLines {
  readonly mesh: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  private tick = -1;

  constructor() {
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG, uniforms: { viewport: { value: new THREE.Vector2(1, 1) } },
      transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.OneMinusSrcAlphaFactor, blendEquation: THREE.AddEquation,
    });
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
    this.mesh.frustumCulled = false;
    // After the GameMID layer's meshes and particles (particles.ts gameMID 1e6), before
    // ParticlesFront (2e6).
    this.mesh.renderOrder = 1.5e6;
  }

  /** The lines of frame `tick`: its step's LINE events (kept while the same frame is redrawn). */
  update(tick: number, events: SoundEvent[], width: number, height: number): void {
    this.mesh.material.uniforms.viewport.value.set(width, height);
    if (tick === this.tick) return;
    this.tick = tick;
    const lines: LineEvent[] = [];
    for (const e of events) if (e.kind === LINE && e.line && e.line.tick === tick) lines.push(e.line);
    const n = lines.length;
    const pos = new Float32Array(n * 12), other = new Float32Array(n * 12), side = new Float32Array(n * 4);
    const colour = new Float32Array(n * 16), lineWidth = new Float32Array(n * 4), index: number[] = [];
    lines.forEach((l, i) => {
      for (let k = 0; k < 4; k++) {
        const atStart = k < 2, v = i * 4 + k;
        pos.set(atStart ? l.start : l.end, v * 3);
        other.set(atStart ? l.end : l.start, v * 3);
        side[v] = k % 2 ? 1 : -1;
        colour.set(l.colour, v * 4);
        lineWidth[v] = l.width;
      }
      const v = i * 4;
      index.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('other', new THREE.BufferAttribute(other, 3));
    g.setAttribute('side', new THREE.BufferAttribute(side, 1));
    g.setAttribute('lineColour', new THREE.BufferAttribute(colour, 4));
    g.setAttribute('lineWidth', new THREE.BufferAttribute(lineWidth, 1));
    g.setIndex(index);
    this.mesh.geometry.dispose();
    this.mesh.geometry = g;
    this.mesh.visible = n > 0;
  }
}
